import { Field, FieldArray, FieldProps, Form, Formik, getIn } from 'formik';
import React, { useEffect, useRef } from 'react';
import ClassicEditor from '@rastech-inc/ckeditor';
import ActionLink from 'components/ActionLink';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';
import { Mention, MentionsInput } from 'react-mentions';
import CKEditorField, {
  CkEditorBaseConfig,
} from '../../../../CKEditorField/CKEditorField';
import LoadingCircleIndicator from '../../../../LoadingCircleIndicator/LoadingCircleIndicator';
import { IOption } from '../../../../Select/Select';
import CompanyMemberSelect from './CompanyMemberSelect/CompanyMemberSelect';
// import { HintIcon, HintText, QuestionHintBox } from './GeneralQuestion/styles';
import { Container, Delete, NotifContainer } from './styles';
import {
  getChoices,
  IMultipleChoiceQuestionView,
  INotifGroup,
  ISurveyView,
  PeriodType,
} from 'store/survey/surveyTypes';
import {
  ScoreMethodType,
  VisibilityStrategy,
  InstanceType,
} from 'store/survey/surveyTypes';
import {
  ICompany,
  ICompanyMember,
  ICompanyRole,
} from 'store/companies/companiesTypes';
import { ITags } from 'store/providers/providerTypes';
import { requireSeveralUserFn } from '../../../../../store/users/usersSlice';
import { isANumber } from '../../../../../utils/number';
import i18n from '../../../../../i18n';
import ConfigSelector, {
  IConfig,
} from '../../../../ConfigSelector/ConfigSelector';
import { orange } from '../../../../../utils/style/colors';
import { MultiDatePicker } from 'components/MyDatePicker/MultiDatePicker';

interface ISurveySettingsFormProps {
  saving: boolean;
  onSubmit: (values: ISurveyView) => void;
  initialName: string;
  initialFields: Array<{ name: string; value: string }>;
  initialAskTitle: boolean;
  initialAutoLock: boolean;
  initialKeepUnique: boolean;
  initialPeriodType: PeriodType;
  initialDescription: string;
  initialUniquePerTime: boolean;
  initialUniquePerUser: boolean;
  initialKeepResponsesAnonymous: boolean;
  initialKeepLatestInstanceIfAnonymous: boolean;
  initialRecommendRefill: boolean;
  initialUniquePerInstance: boolean;
  initialRandomizeOrder: boolean;
  initialKeepSubmitQuestionList: boolean;
  initialUniquePerTags: boolean;
  initialVersionTitle: string;
  initialVersionDescription: string;
  initialScoreMethod: ScoreMethodType;
  initialTrackResponseChanges: boolean;
  initialVisibilityStrategy: VisibilityStrategy;
  initialShowResponseToTarget: boolean;
  initialOfferToFillAtFirst: boolean;
  initialSensitiveChangeRequiresEditor: boolean;
  initialStageQuestion: number | undefined;
  initialFillValidPeriod: {
    fromOffsetDaysRelativeToStartOfPeriod: number | null;
    toOffsetDaysRelativeToEndOfPeriod: number | null;
  };
  instanceType: InstanceType;
  readOnly: boolean;
  tags: ITags['data'] | undefined;
  survey: ISurveyView;
  companyMembers?: ICompany['members'];
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  onlyEditNotifGroup?: boolean;
  token: string;
  companyRoles?: { [key: number]: ICompanyRole };
  supportOrOwner: boolean;
  isAdvancedAdmin: boolean;
}

const SurveySettingForm: React.FC<ISurveySettingsFormProps> = ({
  saving,
  onSubmit,
  initialName,
  initialFields,
  initialAskTitle,
  initialAutoLock,
  initialKeepUnique,
  initialTrackResponseChanges,
  initialPeriodType,
  initialDescription,
  initialUniquePerTime,
  initialUniquePerUser,
  initialRecommendRefill,
  initialUniquePerInstance,
  initialUniquePerTags,
  initialRandomizeOrder,
  initialKeepSubmitQuestionList,
  initialKeepResponsesAnonymous,
  initialScoreMethod,
  initialVisibilityStrategy,
  initialShowResponseToTarget,
  initialVersionTitle,
  initialVersionDescription,
  initialStageQuestion,
  initialOfferToFillAtFirst,
  initialSensitiveChangeRequiresEditor,
  initialKeepLatestInstanceIfAnonymous,
  initialFillValidPeriod,
  readOnly,
  instanceType,
  survey,
  companyMembers,
  requireSeveralUser,
  tags,
  onlyEditNotifGroup,
  token,
  companyRoles,
  supportOrOwner,
  isAdvancedAdmin,
}) => {
  const availableConfigs = React.useMemo(() => {
    const allConfigs: IConfig[] = [
      {
        key: 'conf_submitSuccessMessage',
        label:
          i18n.t('conf_submitSuccessMessage', { ns: 'survey' }) ||
          'Submit Success Message',
        value: '',
        type: 'text',
      },
      {
        key: 'conf_submitSuccessActionTitle',
        label:
          i18n.t('conf_submitSuccessActionTitle', { ns: 'survey' }) ||
          'Submit Success Action Title',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_submitSuccessActionUrl',
        label:
          i18n.t('conf_submitSuccessActionUrl', { ns: 'survey' }) ||
          'Submit Success Action URL',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_newResponse',
        label: i18n.t('conf_newResponse', { ns: 'survey' }) || 'New Response',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_allowDraft',
        label: i18n.t('conf_allowDraft', { ns: 'survey' }) || 'Allow Draft',
        value: false,
        type: 'boolean',
      },
      {
        key: 'conf_addDescriptionTitle',
        label:
          i18n.t('conf_addDescriptionTitle', { ns: 'survey' }) ||
          'conf_addDescriptionTitle',
        value: '',
        advanced: true,
        type: 'text',
      },
      {
        key: 'conf_uniquePerDevice',
        label:
          i18n.t('conf_uniquePerDevice', { ns: 'survey' }) ||
          'conf_uniquePerDevice',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_openNextOnFill',
        label:
          i18n.t('conf_openNextOnFill', { ns: 'survey' }) ||
          'Open Next On Fill',
        value: false,
        type: 'boolean',
        advanced: true,
      },
      {
        key: 'conf_shouldOpenNextMessage',
        label:
          i18n.t('conf_shouldOpenNextMessage', { ns: 'survey' }) ||
          'Should Open Next Message',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_signOutOnFill',
        label:
          i18n.t('conf_signOutOnFill', { ns: 'survey' }) || 'Sign Out On Fill',
        value: true,
        type: 'boolean',
        advanced: true,
      },
      {
        key: 'conf_allowAnonymousUsers',
        label:
          i18n.t('conf_allowAnonymousUsers', { ns: 'survey' }) ||
          'Allow Anonymous Users',
        value: true,
        type: 'boolean',
        advanced: true,
      },
      {
        key: 'conf_skipSurveyTag',
        label:
          i18n.t('conf_skipSurveyTag', { ns: 'survey' }) || 'Skip Survey Tag',
        value: true,
        type: 'boolean',
        advanced: true,
      },
      {
        key: 'conf_emptyMessage',
        label: i18n.t('conf_emptyMessage', { ns: 'survey' }) || 'Empty Message',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_excludeDetailedGridReports',
        label:
          i18n.t('conf_excludeDetailedGridReports', { ns: 'survey' }) ||
          'exclude Detailed Grid Reports',
        value: true,
        type: 'boolean',
      },
      {
        key: 'conf_reportNamePrefix',
        label:
          i18n.t('conf_reportNamePrefix', { ns: 'survey' }) ||
          'Report Name Prefix',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_relatedUserTitle',
        label:
          i18n.t('conf_relatedUserTitle', { ns: 'survey' }) ||
          'Related User Title',
        value: '',
        type: 'text',
      },
      {
        key: 'conf_loginMessage',
        label: i18n.t('conf_loginMessage', { ns: 'survey' }) || 'Login Message',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_openHints',
        label: i18n.t('conf_openHints', { ns: 'survey' }) || 'Open Hints',
        value: true,
        type: 'boolean',
        advanced: true,
      },
      {
        key: 'conf_nonNumbering',
        label: i18n.t('conf_nonNumbering', { ns: 'survey' }) || 'Non Numbering',
        value: true,
        type: 'boolean',
      },
      {
        key: 'conf_dataSource',
        label: i18n.t('conf_dataSource', { ns: 'survey' }) || 'Data Source',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_AIFillSurveyQuestion',
        label:
          i18n.t('conf_AIFillSurveyQuestion', { ns: 'survey' }) ||
          'Filling out the survey question',
        value: true,
        type: 'boolean',
        advanced: true,
      },
      {
        key: 'conf_responseMessageBasedOnScore',
        label:
          i18n.t('conf_responseMessageBasedOnScore', { ns: 'survey' }) ||
          'Submit Success Message',
        value:
          '{"0-8": "نمره پایین تر از سطح انتظار است", "9-15": "نمره شما متوسط است", "16-20": "نمره شما عالی است"}',
        type: 'text',
      },
      {
        key: 'conf_printTemplate',
        label:
          i18n.t('conf_printTemplate', { ns: 'survey' }) || 'Print Template',
        value: '',
        type: 'text',
        advanced: true,
      },
      {
        key: 'conf_showFollowUpQuestionBasedOnScore',
        label:
          i18n.t('conf_showFollowUpQuestionBasedOnScore', { ns: 'survey' }) ||
          'Submit Success Message',
        value: '',
        type: 'text',
      },
      {
        key: 'conf_submitButtonTitle',
        label:
          i18n.t('conf_submitButtonTitle', { ns: 'survey' }) ||
          'Submit Botton Title',
        value: '',
        type: 'text',
      },
      {
        key: 'conf_hideQuestionTags',
        label: i18n.t('conf_hideQuestionTags', { ns: 'survey' }) || 'Hidden',
        value: true,
        type: 'boolean' as const,
      },
      {
        key: 'conf_addDescriptionButton',
        label:
          i18n.t('conf_addDescriptionButton', { ns: 'survey' }) ||
          'conf_addDescriptionButton',
        value: true,
        advanced: true,
        type: 'boolean',
      },
      {
        key: 'conf_htmlResponseDocumentUrl',
        label:
          i18n.t('conf_htmlResponseDocumentUrl', { ns: 'survey' }) ||
          'conf_addDescriptionButton',
        value: '',
        advanced: true,
        type: 'text',
      },
      {
        key: 'conf_AIFillOutOtherFieldsBeforeSubmit',
        label:
          i18n.t('conf_AIFillOutOtherFieldsBeforeSubmit', { ns: 'survey' }) ||
          'conf_AIFillOutOtherFieldsBeforeSubmit',
        value: 'true',
        advanced: true,
        type: 'boolean',
      },
      {
        key: 'conf_surveySystemPrompt',
        label:
          i18n.t('conf_surveySystemPrompt', { ns: 'survey' }) ||
          'conf_surveySystemPrompt',
        value: '',
        advanced: true,
        type: 'text',
      },
    ];

    return supportOrOwner
      ? allConfigs
      : allConfigs.filter((config) => !config.advanced);
  }, [supportOrOwner]);

  const initialSelectedConfigs = React.useMemo(() => {
    return initialFields
      .filter((field) => field.name && field.name.startsWith('conf_'))
      .map((field) => {
        const config = availableConfigs.find((c) => c.key === field.name);
        if (config) {
          return {
            key: field.name,
            label: config.label,
            value:
              config.type === 'boolean' ? field.value === 'true' : field.value,
            type: config.type,
          };
        }
        return null;
      })
      .filter(Boolean) as IConfig[];
  }, [initialFields, availableConfigs]);

  const [selectedConfigs, setSelectedConfigs] = React.useState<IConfig[]>(
    initialSelectedConfigs
  );

  const handleConfigAdd = (config: {
    key: string;
    label: string;
    value: string | boolean;
    type: 'text' | 'boolean';
  }) => {
    const newSelectedConfigs = [...selectedConfigs, config];
    setSelectedConfigs(newSelectedConfigs);
  };

  const handleConfigRemove = (config: {
    key: string;
    label: string;
    value: string | boolean;
    type: 'text' | 'boolean';
  }) => {
    const newSelectedConfigs = selectedConfigs.filter(
      (c) => c.key !== config.key
    );
    setSelectedConfigs(newSelectedConfigs);
  };

  const handleConfigValueChange = (
    configKey: string,
    newValue: string | boolean
  ) => {
    const newSelectedConfigs = selectedConfigs.map((config) =>
      config.key === configKey ? { ...config, value: newValue } : config
    );
    setSelectedConfigs(newSelectedConfigs);
  };

  const periodType = [
    { value: 'none', label: i18n.t('disabled', { ns: 'survey' }) as string },
    { value: 'day', label: i18n.t('day', { ns: 'survey' }) as string },
    { value: 'week', label: i18n.t('week', { ns: 'survey' }) as string },
    { value: 'month', label: i18n.t('month', { ns: 'survey' }) as string },
    { value: 'season', label: i18n.t('season', { ns: 'survey' }) as string },
    {
      value: 'twoSeason',
      label: i18n.t('twoSeason', { ns: 'survey' }) as string,
    },
    { value: 'year', label: i18n.t('year', { ns: 'survey' }) as string },
  ];

  const scoreMethodOptions = [
    { value: 'sum', label: i18n.t('sum', { ns: 'survey' }) as string },
    { value: 'max', label: i18n.t('max', { ns: 'survey' }) as string },
    {
      value: 'totalAverage',
      label: i18n.t('totalAverage', { ns: 'survey' }) as string,
    },
  ];
  const mediums = [
    { value: 'SMS', label: i18n.t('SMS', { ns: 'survey' }) as string },
    { value: 'Slack', label: i18n.t('Slack', { ns: 'survey' }) as string },
    { value: 'Hook', label: i18n.t('Hook', { ns: 'survey' }) as string },
  ];

  const actions = [
    { value: 'Submit', label: i18n.t('Submit', { ns: 'survey' }) as string },
    { value: 'Update', label: i18n.t('Update', { ns: 'survey' }) as string },
    { value: 'Draft', label: i18n.t('Draft', { ns: 'survey' }) as string },
    { value: 'Undraft', label: i18n.t('Undraft', { ns: 'survey' }) as string },
    {
      value: 'DraftSubmit',
      label: i18n.t('DraftSubmit', { ns: 'survey' }) as string,
    },
    { value: 'Lock', label: i18n.t('Lock', { ns: 'survey' }) as string },
    { value: 'Unlock', label: i18n.t('Unlock', { ns: 'survey' }) as string },
    {
      value: 'UpdateByAdmin',
      label: i18n.t('UpdateByAdmin', { ns: 'survey' }) as string,
    },
    {
      value: 'AnswerChanged',
      label: i18n.t('AnswerChanged', { ns: 'survey' }) as string,
    },
    {
      value: 'Scheduled',
      label: i18n.t('Scheduled', { ns: 'survey' }) as string,
    },
  ];

  const periodTypes = [
    { value: 'day', label: i18n.t('daily', { ns: 'survey' }) },
    { value: 'week', label: i18n.t('weekly', { ns: 'survey' }) },
    { value: 'month', label: i18n.t('monthly', { ns: 'survey' }) },
    { value: 'season', label: i18n.t('season', { ns: 'survey' }) },
    { value: 'twoSeason', label: i18n.t('twoSeason', { ns: 'survey' }) },
    { value: 'year', label: i18n.t('yearly', { ns: 'survey' }) },
  ];

  const weekOffset = [
    { value: 0, label: i18n.t('saturday', { ns: 'survey' }) },
    { value: 1, label: i18n.t('sunday', { ns: 'survey' }) },
    { value: 2, label: i18n.t('monday', { ns: 'survey' }) },
    { value: 3, label: i18n.t('tuesday', { ns: 'survey' }) },
    { value: 4, label: i18n.t('wednesday', { ns: 'survey' }) },
    { value: 5, label: i18n.t('thursday', { ns: 'survey' }) },
    { value: 6, label: i18n.t('friday', { ns: 'survey' }) },
  ];

  const monthOffset = Array.from(
    {
      length: 30,
    },
    (_, index) => index + 1
  ).map((el: number) => ({
    value: el,
    label: i18n.t('thOfMonthDay', { ns: 'survey', number: el }),
  }));

  const seasonOffset = Array.from(
    {
      length: 90,
    },
    (_, index) => index + 1
  ).map((el: number) => ({ value: el, label: el.toString() }));

  const twoSeasonOffset = Array.from(
    {
      length: 180,
    },
    (_, index) => index + 1
  ).map((el: number) => ({ value: el, label: el.toString() }));

  const yearOffset = Array.from(
    {
      length: 365,
    },
    (_, index) => index + 1
  ).map((el: number) => ({ value: el, label: el.toString() }));

  const initialValues = {
    name: initialName || '',
    fields: initialFields || [],
    askTitle: initialAskTitle || false,
    offerToFillAtFirst: initialOfferToFillAtFirst || false,
    sensitiveChangeRequiresEditor:
      initialSensitiveChangeRequiresEditor || false,
    autoLock: initialAutoLock || false,
    description: initialDescription || '',
    keepUnique: initialKeepUnique || false,
    trackResponseChanges: initialTrackResponseChanges || false,
    periodType: initialPeriodType || 'none',
    uniquePerTime: initialUniquePerTime || false,
    uniquePerUser: initialUniquePerUser || false,
    recommendRefill: initialRecommendRefill || false,
    uniquePerInstance: initialUniquePerInstance || false,
    randomizeOrder: initialRandomizeOrder || false,
    keepSubmitQuestionList: initialKeepSubmitQuestionList || false,
    uniquePerTags: initialUniquePerTags || false,
    keepResponsesAnonymous: initialKeepResponsesAnonymous || false,
    keepLatestInstanceIfAnonymous:
      initialKeepLatestInstanceIfAnonymous || false,
    scoreMethod: initialScoreMethod || 'totalAverage',
    currentVersion: '',
    visibilityStrategy: initialVisibilityStrategy,
    showResponseToTarget: initialShowResponseToTarget,
    versionTitle: initialVersionTitle,
    versionDescription: initialVersionDescription,
    stageQuestion: initialStageQuestion,
    versions: {},
    notifications: survey.notifications,
    notifGroups: survey.notifGroups,
    allowEditNotifGroupMembersTo: survey.allowEditNotifGroupMembersTo,
    restrictionOfCompletion:
      isANumber(initialFillValidPeriod.fromOffsetDaysRelativeToStartOfPeriod) ||
      isANumber(initialFillValidPeriod.toOffsetDaysRelativeToEndOfPeriod),
    fillValidPeriod: initialFillValidPeriod,
  };

  const visibilityStrategyOptions = [
    {
      value: 'open',
      label: i18n.t('visibility_open', { ns: 'survey' }) as string,
    },
    {
      value: 'protected',
      label: i18n.t('visibility_protected', { ns: 'survey' }) as string,
    },
  ];
  if (instanceType === 'User') {
    visibilityStrategyOptions.push({
      value: 'personal',
      label: i18n.t('visibility_personal', { ns: 'survey' }) as string,
    });
  }
  const form = useRef<any>(null);

  useEffect(() => {
    if (!saving) {
      form.current && form.current.resetForm();
    }
  });

  useEffect(() => {
    if (form.current) {
      const currentFields = getIn(form.current.values, 'fields') || [];
      const configFields = selectedConfigs.map((config) => ({
        name: config.key,
        value:
          config.type === 'boolean' ? config.value.toString() : config.value,
      }));
      const nonConfigFields = currentFields.filter(
        (field: any) => !field.name || !field.name.startsWith('conf_')
      );
      form.current.setFieldValue('fields', [
        ...nonConfigFields,
        ...configFields,
      ]);
    }
  }, [selectedConfigs]);

  const questions = Object.values(survey.questionMap!).reduce(
    (current: IOption[], item: any) => {
      if (item.data?.type === 'multipleChoice') {
        if (!item.data.deleted)
          current.push({
            value: item.data.id,
            label: item.data.title,
          });
      }
      return current;
    },
    [] as IOption[]
  );

  const targetTypes = [
    { value: -1, label: i18n.t('sender', { ns: 'survey' }) as string },
    { value: -2, label: i18n.t('relatedUser', { ns: 'survey' }) as string },
    { value: -3, label: i18n.t('notifGroupTitle', { ns: 'survey' }) as string },
    { value: 0, label: i18n.t('fixedPerson', { ns: 'survey' }) as string },
  ];
  if (companyRoles)
    targetTypes.push({
      value: -4,
      label: i18n.t('roles', { ns: 'survey' }) as string,
    });

  const notificationsPatternData = [
    { id: 'title', display: 'توضیح پاسخ' },
    { id: 'sender', display: 'ارسال کننده پاسخ' },
    { id: 'actor', display: 'کاربر عامل' },
    { id: 'instanceUser', display: 'کاربر مرتبط' },
    { id: 'sd', display: 'تاریخ ارسال' },
    { id: 'sdh', display: 'تاریخ ارسال به همراه ساعت' },
    { id: 'ud', display: 'تاریخ به روز رسانی' },
    { id: 'udh', display: 'تاریخ به روز رسانی به همراه ساعت' },
    { id: 'ed', display: 'بازه مرتبط' },
    { id: 'dl', display: 'لینک مستقیم به پاسخ' },
    { id: 'responseId', display: 'شناسه پاسخ' },
  ];
  Object.values(survey.questionMap || {}).forEach((item) => {
    if (item.data && !item.data?.deleted)
      notificationsPatternData.push({
        id: 'a:' + item.data.id + '',
        display: item.data.title,
      });
  });

  const companyRolesOptions = Object.values(companyRoles || {})?.reduce(
    (temp: IOption[], roleItem: ICompanyRole | undefined) => {
      if (!roleItem || roleItem.isProjectRole) return temp;
      temp.push({
        value: roleItem.id,
        label: roleItem.name,
      });
      return temp;
    },
    [] as IOption[]
  );

  return (
    <Formik
      ref={form}
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({
        values: {
          offerToFillAtFirst,
          sensitiveChangeRequiresEditor,
          autoLock,
          keepUnique,
          trackResponseChanges,
          uniquePerUser,
          keepResponsesAnonymous,
          recommendRefill,
          uniquePerInstance,
          showResponseToTarget,
          notifications,
          notifGroups,
          restrictionOfCompletion,
        },
      }) => {
        const editNotifGroup = (
          <Container>
            <h6>{i18n.t('notifGroups', { ns: 'survey' }) as string}</h6>
            <FieldArray
              name="notifGroups"
              render={({ push, remove }) => (
                <div>
                  {!onlyEditNotifGroup && (
                    <ActionLink
                      color="blue"
                      onClick={() =>
                        push({
                          userIds: [],
                          groupTitle: '',
                          groupId: (Math.random() + 1)
                            .toString(36)
                            .substring(2),
                        })
                      }
                    >
                      {i18n.t('addNotifGroup', { ns: 'survey' }) as string}
                    </ActionLink>
                  )}
                  {notifGroups?.map((_item, index) => (
                    <NotifContainer key={`notifGroups[${index}]name`}>
                      <Delete
                        type="x"
                        color="red"
                        onClick={() => remove(index)}
                      />
                      <Field
                        name={`notifGroups.${index}.groupTitle`}
                        render={({ field }: FieldProps) => (
                          <div style={{ flex: '1' }}>
                            <Input
                              type="text"
                              {...field}
                              id={`notifGroups[${index}]title`}
                              label={
                                i18n.t('notifGroupTitle', {
                                  ns: 'survey',
                                }) as string
                              }
                              value={field.value || ''}
                              autoComplete="off"
                              disabled={readOnly}
                              wrapperClassName="value-field"
                            />
                          </div>
                        )}
                      />

                      <Field
                        name={`notifGroups.${index}.userIds`}
                        render={({ form, field }: FieldProps) => (
                          <div style={{ flex: '3' }}>
                            {companyMembers ? (
                              <CompanyMemberSelect
                                companyMembers={companyMembers!}
                                onSelectUser={(userIds: number[]) => {
                                  form.setFieldValue(
                                    `notifGroups.${index}.userIds`,
                                    userIds
                                  );
                                }}
                                label={
                                  i18n.t('targetUser', {
                                    ns: 'survey',
                                  }) as string
                                }
                                placeholder={
                                  i18n.t('targetUser', {
                                    ns: 'survey',
                                  }) as string
                                }
                                selected={field.value ? field.value : []}
                                multiSelect
                                requireSeveralUser={requireSeveralUser}
                              />
                            ) : (
                              <LoadingCircleIndicator />
                            )}
                          </div>
                        )}
                      />

                      <Field
                        name={`notifGroups.${index}.roleIds`}
                        render={({ form, field }: FieldProps) => (
                          <div style={{ flex: '3' }}>
                            {companyRoles ? (
                              <Select
                                label={
                                  i18n.t('targetRoles', {
                                    ns: 'survey',
                                  }) as string
                                }
                                onChangeValues={(values: IOption[]) => {
                                  form.setFieldValue(
                                    `notifGroups.${index}.roleIds`,
                                    values.map((role) => role.value)
                                  );
                                }}
                                options={companyRolesOptions}
                                values={companyRolesOptions.filter(
                                  (role: IOption) =>
                                    field?.value?.includes(role.value) ?? false
                                )}
                                multiChoice={true}
                              />
                            ) : (
                              <LoadingCircleIndicator />
                            )}
                          </div>
                        )}
                      />
                    </NotifContainer>
                  ))}
                </div>
              )}
            />
            {!onlyEditNotifGroup && (
              <div>
                <Field
                  name={'allowEditNotifGroupMembersTo'}
                  render={({ form, field }: FieldProps) => (
                    <div style={{ flex: '3' }}>
                      {companyMembers ? (
                        <CompanyMemberSelect
                          companyMembers={companyMembers!}
                          onSelectUser={(userIds: number[]) => {
                            form.setFieldValue(
                              'allowEditNotifGroupMembersTo',
                              userIds
                            );
                          }}
                          label={
                            i18n.t('allowEditNotifGroupMembersTo', {
                              ns: 'survey',
                            }) as string
                          }
                          placeholder={
                            i18n.t('users', { ns: 'survey' }) as string
                          }
                          selected={field.value || []}
                          multiSelect
                          requireSeveralUser={requireSeveralUser}
                        />
                      ) : (
                        <LoadingCircleIndicator />
                      )}
                    </div>
                  )}
                />
              </div>
            )}
          </Container>
        );
        if (onlyEditNotifGroup)
          return (
            //  @ts-expect-error JSX error
            <Form>
              {editNotifGroup}
              <div className="col s12">
                <Button loading={saving} type="submit" color="green">
                  {i18n.t('save', { ns: 'survey' }) as string}
                </Button>
              </div>
            </Form>
          );
        return (
          //  @ts-expect-error JSX error
          <Form>
            <div className="input-field col s12">
              <Field
                name="name"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    disabled={readOnly}
                    id="name"
                    type="text"
                    label={i18n.t('surveyName', { ns: 'survey' }) as string}
                    placeholder={
                      i18n.t('surveyName', { ns: 'survey' }) as string
                    }
                    autoComplete="off"
                  />
                )}
              />
            </div>
            <Container>
              <div className="input-field col s12">
                <Field
                  name="description"
                  render={({ form, field }: FieldProps) => (
                    <CKEditorField
                      label={
                        i18n.t('surveyDescription', { ns: 'survey' }) as string
                      }
                      style={{
                        '--ck-focus-ring': '0px',
                      }}
                      editor={ClassicEditor}
                      data={field.value}
                      // updateContent={(content: string) => {
                      //   form.setFieldValue('description', content);
                      // }}
                      onChange={(content: string) => {
                        form.setFieldValue('description', content);
                      }}
                      dir="rtl"
                      config={CkEditorBaseConfig(token)}
                    />
                  )}
                />
              </div>
              <div>
                <ConfigSelector
                  availableConfigs={availableConfigs.filter(
                    (c) => !selectedConfigs.find((s) => s.key === c.key)
                  )}
                  selectedConfigs={selectedConfigs}
                  onAdd={handleConfigAdd}
                  onRemove={handleConfigRemove}
                  onValueChange={handleConfigValueChange}
                />
              </div>
            </Container>
            <Container>
              <FieldArray
                name="notifications"
                render={({ push, remove }) => (
                  <div>
                    {!readOnly && (
                      <ActionLink
                        color="blue"
                        onClick={() =>
                          push({
                            userId: -1,
                            action: 'Submit',
                            messagePattern: '',
                            medium: 'SMS',
                          })
                        }
                      >
                        {
                          i18n.t('addNotification', {
                            ns: 'survey',
                          }) as string
                        }
                      </ActionLink>
                    )}
                    {notifications.map((_item, index) => (
                      <NotifContainer key={`notifications[${index}]name`}>
                        <Delete
                          type="x"
                          color="red"
                          onClick={() => remove(index)}
                        />
                        <div className="flex-1 flex gap-5">
                          <Field
                            name={`notifications.${index}.medium`}
                            render={({ form, field }: FieldProps) => (
                              <div style={{ flex: '1' }}>
                                <div>
                                  <Select
                                    options={mediums}
                                    disabled={readOnly}
                                    isRequired={true}
                                    allowRemove={false}
                                    values={
                                      field.value
                                        ? [
                                            {
                                              value: field.value,
                                              label: i18n.t(`${field.value}`, {
                                                ns: 'survey',
                                              }) as string,
                                            },
                                          ]
                                        : []
                                    }
                                    onChangeValues={(fieldOption) => {
                                      form.setFieldValue(
                                        `notifications.${index}.medium`,
                                        fieldOption[0]
                                          ? fieldOption[0].value
                                          : null
                                      );
                                    }}
                                    placeholder={
                                      i18n.t('medium', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                    label={
                                      i18n.t('medium', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  />
                                  {field.value === 'Slack' ||
                                    (field.value === 'Hook' && (
                                      <Input
                                        type={'text'}
                                        id={`item[${index}]slackABC`}
                                        label={
                                          i18n.t(
                                            field.value === 'Slack'
                                              ? 'slackABC'
                                              : 'HookUrl',
                                            {
                                              ns: 'survey',
                                            }
                                          ) as string
                                        }
                                        value={
                                          form.values.notifications[index]
                                            .slackHookABC || ''
                                        }
                                        onChange={(e) => {
                                          form.setFieldValue(
                                            `notifications.${index}.slackHookABC`,
                                            e.target.value
                                          );
                                        }}
                                        autoComplete="off"
                                        disabled={readOnly}
                                        wrapperClassName="value-field"
                                      />
                                    ))}
                                </div>
                              </div>
                            )}
                          />
                          <Field
                            name={`notifications.${index}.action`}
                            render={({ form, field }: FieldProps) => (
                              <div style={{ flex: '1' }}>
                                <div>
                                  <Select
                                    options={actions}
                                    disabled={readOnly}
                                    isRequired={true}
                                    allowRemove={false}
                                    values={
                                      field.value
                                        ? [
                                            {
                                              value: field.value,
                                              label: i18n.t(`${field.value}`, {
                                                ns: 'survey',
                                              }) as string,
                                            },
                                          ]
                                        : []
                                    }
                                    onChangeValues={(fieldOption) => {
                                      form.setFieldValue(
                                        `notifications.${index}.action`,
                                        fieldOption[0]
                                          ? fieldOption[0].value
                                          : null
                                      );
                                    }}
                                    placeholder={
                                      i18n.t('action', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                    label={
                                      i18n.t('action', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  />
                                  {field.value === 'AnswerChanged' && (
                                    <Select
                                      options={questions}
                                      disabled={readOnly}
                                      isRequired={true}
                                      allowRemove={false}
                                      values={
                                        form.values.notifications[index]
                                          .changedQuestionId
                                          ? [
                                              {
                                                value:
                                                  form.values.notifications[
                                                    index
                                                  ].changedQuestionId,
                                                label:
                                                  survey.questionMap![
                                                    form.values.notifications[
                                                      index
                                                    ].changedQuestionId
                                                  ].data.title,
                                              },
                                            ]
                                          : []
                                      }
                                      onChangeValues={(fieldOption) => {
                                        if (
                                          !fieldOption[0] ||
                                          form.values.notifications[index]
                                            .changedQuestionId !==
                                            fieldOption[0].value
                                        ) {
                                          form.setFieldValue(
                                            `notifications.${index}.changedQuestionValue`,
                                            null
                                          );
                                          form.setFieldValue(
                                            `notifications.${index}.changedQuestionId`,
                                            fieldOption[0]
                                              ? fieldOption[0].value
                                              : null
                                          );
                                        }
                                      }}
                                      placeholder={
                                        i18n.t('changedQuestion', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      label={
                                        i18n.t('changedQuestion', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    />
                                  )}
                                  {field.value === 'AnswerChanged' &&
                                    form.values.notifications[index]
                                      .changedQuestionId &&
                                    (() => {
                                      const choices = getChoices(
                                        survey!.questionMap![
                                          form.values.notifications[index]!
                                            .changedQuestionId!
                                        ].data! as IMultipleChoiceQuestionView,
                                        tags!
                                      ).reduce(
                                        (current: IOption[], item: any) => {
                                          current.push({
                                            value: item.value,
                                            label:
                                              item.text || 'no-label-found',
                                          });
                                          return current;
                                        },
                                        [] as IOption[]
                                      );
                                      return (
                                        <Select
                                          options={choices}
                                          disabled={readOnly}
                                          isRequired={true}
                                          allowRemove={false}
                                          values={
                                            form.values.notifications[index]
                                              .changedQuestionValue
                                              ? [
                                                  {
                                                    value:
                                                      form.values.notifications[
                                                        index
                                                      ].changedQuestionValue,
                                                    label: (
                                                      choices.find(
                                                        (it) =>
                                                          it.value ===
                                                          form.values
                                                            .notifications[
                                                            index
                                                          ].changedQuestionValue
                                                      ) || { label: 'removed' }
                                                    ).label,
                                                  },
                                                ]
                                              : []
                                          }
                                          onChangeValues={(fieldOption) => {
                                            form.setFieldValue(
                                              `notifications.${index}.changedQuestionValue`,
                                              fieldOption[0]
                                                ? fieldOption[0].value
                                                : null
                                            );
                                          }}
                                          placeholder={
                                            i18n.t('changedQuestionValue', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                          label={
                                            i18n.t('changedQuestionValue', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                        />
                                      );
                                    })()}

                                  {field.value === 'Scheduled' && (
                                    <div>
                                      <Select
                                        values={periodType.filter(
                                          (el: IOption) =>
                                            el.value ===
                                            form.values.notifications[index]
                                              ?.schedulePeriod?.periodType
                                        )}
                                        options={periodTypes}
                                        onChangeValues={(fieldOption) => {
                                          form.setFieldValue(
                                            `notifications.${index}.schedulePeriod.periodType`,
                                            fieldOption[0]
                                              ? fieldOption[0].value
                                              : null
                                          );
                                          form.setFieldValue(
                                            `notifications.${index}.schedulePeriod.periodType`,
                                            fieldOption[0]
                                              ? fieldOption[0].value
                                              : null
                                          );
                                        }}
                                        label={
                                          i18n.t('notifPeriodType', {
                                            ns: 'survey',
                                          }) as string
                                        }
                                      />
                                      <MultiDatePicker
                                        id={`notifications.${index}.schedulePeriod.startDate`}
                                        setDate={(value) => {
                                          form.setFieldValue(
                                            `notifications.${index}.schedulePeriod.startDate`,
                                            value
                                          );
                                        }}
                                        value={
                                          Number(
                                            form.values.notifications[index]
                                              ?.schedulePeriod?.startDate
                                          ) ?? null
                                        }
                                        placeholder={
                                          i18n.t('notifStartDate', {
                                            ns: 'survey',
                                          }) as string
                                        }
                                        label={
                                          i18n.t('notifStartDate', {
                                            ns: 'survey',
                                          }) as string
                                        }
                                        hasTime={true}
                                      />
                                      <MultiDatePicker
                                        // className="col s12 m12 roundDateInputInTask"
                                        id={`notifications.${index}.schedulePeriod.endDate`}
                                        setDate={(value) => {
                                          if (value) {
                                            const formattedDate = new Date(
                                              value
                                            ).setHours(0);
                                            console.log(formattedDate);
                                            form.setFieldValue(
                                              `notifications.${index}.schedulePeriod.endDate`,
                                              formattedDate
                                            );
                                          }
                                        }}
                                        value={
                                          Number(
                                            form.values.notifications[index]
                                              ?.schedulePeriod?.endDate
                                          ) ?? null
                                        }
                                        placeholder={
                                          i18n.t('notifEndDate', {
                                            ns: 'survey',
                                          }) as string
                                        }
                                        label={
                                          i18n.t('notifEndDate', {
                                            ns: 'survey',
                                          }) as string
                                        }
                                      />
                                      <>
                                        {[
                                          'week',
                                          'month',
                                          'season',
                                          'twoSeason',
                                          'year',
                                        ].includes(
                                          form.values.notifications[index]
                                            ?.schedulePeriod?.periodType
                                        ) ? (
                                          <>
                                            {(() => {
                                              const periodType =
                                                form.values.notifications[index]
                                                  ?.schedulePeriod?.periodType;

                                              const offsetFormat =
                                                periodType === 'week'
                                                  ? weekOffset
                                                  : periodType === 'month'
                                                    ? monthOffset
                                                    : periodType === 'season'
                                                      ? seasonOffset
                                                      : periodType ===
                                                          'twoSeason'
                                                        ? twoSeasonOffset
                                                        : yearOffset;

                                              return (
                                                <Select
                                                  values={offsetFormat.filter(
                                                    (el) =>
                                                      form.values.notifications[
                                                        index
                                                      ]?.schedulePeriod?.offsetDays?.includes(
                                                        Number(el.value)
                                                      )
                                                  )}
                                                  options={offsetFormat}
                                                  onChangeValues={(
                                                    fieldOption
                                                  ) => {
                                                    form.setFieldValue(
                                                      `notifications.${index}.schedulePeriod.offsetDays`,
                                                      fieldOption?.length
                                                        ? fieldOption.map(
                                                            (el) =>
                                                              Number(el.value)
                                                          )
                                                        : null
                                                    );
                                                  }}
                                                  label={
                                                    i18n.t('notifOffsetDays', {
                                                      ns: 'survey',
                                                      periodType: i18n.t(
                                                        `${periodType}`,
                                                        { ns: 'survey' }
                                                      ),
                                                    }) as string
                                                  }
                                                  multiChoice={true}
                                                />
                                              );
                                            })()}
                                          </>
                                        ) : (
                                          <></>
                                        )}
                                        {form.values.notifications[index]
                                          ?.schedulePeriod?.periodType !==
                                          'week' && (
                                          <Select
                                            values={weekOffset.filter((el) =>
                                              form.values.notifications[
                                                index
                                              ]?.schedulePeriod?.invalidDaysOfWeek?.includes(
                                                Number(el.value)
                                              )
                                            )}
                                            options={weekOffset}
                                            onChangeValues={(fieldOption) => {
                                              form.setFieldValue(
                                                `notifications.${index}.schedulePeriod.invalidDaysOfWeek`,
                                                fieldOption?.length
                                                  ? fieldOption.map((el) =>
                                                      Number(el.value)
                                                    )
                                                  : null
                                              );
                                            }}
                                            label={
                                              i18n.t('invalidDaysOfWeek', {
                                                ns: 'survey',
                                                periodType: periodTypes.find(
                                                  (el) =>
                                                    el.value ===
                                                    form.values.notifications[
                                                      index
                                                    ]?.schedulePeriod
                                                      ?.periodType
                                                )?.label,
                                              }) as string
                                            }
                                            multiChoice={true}
                                          />
                                        )}
                                      </>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Field
                            name={`notifications.${index}.userId`}
                            render={({ form, field }: FieldProps) =>
                              form.values.notifications[index].medium !==
                                'Slack' &&
                              form.values.notifications[index].medium !==
                                'Hook' ? (
                                <div style={{ flex: '1' }}>
                                  <div style={{ flex: '1' }}>
                                    <Select
                                      options={targetTypes}
                                      disabled={readOnly}
                                      isRequired={true}
                                      allowRemove={false}
                                      values={[
                                        targetTypes.find(
                                          (it) =>
                                            it.value ===
                                            (field.value < 0 ? field.value : 0)
                                        )!,
                                      ]}
                                      onChangeValues={(values) => {
                                        const newValue = Number(
                                          values[0].value
                                        );
                                        if (
                                          !(newValue === 0 && field.value > 0)
                                        ) {
                                          form.setFieldValue(
                                            `notifications.${index}.userId`,
                                            newValue
                                          );
                                        }
                                      }}
                                      placeholder={
                                        i18n.t('targetUserType', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      label={
                                        i18n.t('targetUserType', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                    />
                                  </div>
                                  {field.value >= 0 && (
                                    <div style={{ flex: '1' }}>
                                      {companyMembers ? (
                                        <CompanyMemberSelect
                                          companyMembers={companyMembers!}
                                          onSelectUser={(userIds: number[]) => {
                                            form.setFieldValue(
                                              `notifications.${index}.userId`,
                                              userIds?.length > 0
                                                ? userIds[0]
                                                : -1
                                            );
                                          }}
                                          label={
                                            i18n.t('targetUser', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                          placeholder={
                                            i18n.t('targetUser', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                          selected={
                                            field.value <= 0
                                              ? undefined
                                              : [field.value]
                                          }
                                          requireSeveralUser={
                                            requireSeveralUser
                                          }
                                        />
                                      ) : (
                                        <LoadingCircleIndicator />
                                      )}
                                    </div>
                                  )}
                                  {field.value === -3 && (
                                    <div style={{ flex: '1' }}>
                                      {companyMembers ? (
                                        <Select
                                          options={
                                            form.values.notifGroups?.map(
                                              (e: INotifGroup) => ({
                                                value: e.groupId,
                                                label: e.groupTitle,
                                              })
                                            ) || []
                                          }
                                          disabled={readOnly}
                                          isRequired={true}
                                          allowRemove={false}
                                          values={
                                            [
                                              form.values.notifGroups?.find(
                                                (e: INotifGroup) =>
                                                  e.groupId ===
                                                  form.values.notifications[
                                                    index
                                                  ].targetGroup
                                              ),
                                            ]
                                              .filter((e) => e !== undefined)
                                              .map((e: INotifGroup) => ({
                                                value: e.groupId,
                                                label: e.groupTitle,
                                              })) || []
                                          }
                                          onChangeValues={(selected) => {
                                            form.setFieldValue(
                                              `notifications.${index}.targetGroup`,
                                              selected?.length > 0
                                                ? selected[0].value
                                                : undefined
                                            );
                                          }}
                                          placeholder={
                                            i18n.t('notifGroupTitle', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                          label={
                                            i18n.t('notifGroupTitle', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                        />
                                      ) : (
                                        <LoadingCircleIndicator />
                                      )}
                                    </div>
                                  )}
                                  {field.value === -4 && (
                                    <div style={{ flex: '1' }}>
                                      {companyRolesOptions ? (
                                        <div>
                                          <Select
                                            multiChoice={false}
                                            options={companyRolesOptions}
                                            values={companyRolesOptions.filter(
                                              (it) =>
                                                it.value ===
                                                Number(
                                                  form.values.notifications[
                                                    index
                                                  ].targetGroup
                                                )
                                            )}
                                            onChangeValues={(selected) => {
                                              form.setFieldValue(
                                                `notifications.${index}.targetGroup`,
                                                selected?.length > 0
                                                  ? selected[0].value.toString()
                                                  : undefined
                                              );
                                            }}
                                            placeholder={
                                              i18n.t('selectRole', {
                                                ns: 'survey',
                                              }) as string
                                            }
                                            label={
                                              i18n.t('selectRole', {
                                                ns: 'survey',
                                              }) as string
                                            }
                                          />

                                          {form.values.notifications[index]
                                            .targetGroup && (
                                            <div
                                              style={{
                                                fontSize: '12px',
                                                color: orange,
                                                marginTop: '4px',
                                              }}
                                            >
                                              {((): any => {
                                                const selectedRoleId = Number(
                                                  form.values.notifications[
                                                    index
                                                  ].targetGroup
                                                );
                                                const roleCount =
                                                  Object.values(
                                                    companyMembers || {}
                                                  )?.filter(
                                                    (member: ICompanyMember) =>
                                                      Object.values(
                                                        member.roles
                                                      )?.some(
                                                        (role) =>
                                                          role.id ===
                                                          selectedRoleId
                                                      )
                                                  ).length || 0;
                                                return `${i18n.t('numberOfPeopleWithSelectedRole', { ns: 'survey', number: roleCount }) || 'people'}`;
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <LoadingCircleIndicator />
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div />
                              )
                            }
                          />
                          {_item.action !== 'Scheduled' && (
                            <Field
                              name={`notifications.${index}.conditionQuestionId`}
                              render={({ form, field }: FieldProps) => (
                                <div style={{ flex: 1 }}>
                                  <Select
                                    options={questions}
                                    disabled={readOnly}
                                    isRequired={false}
                                    allowRemove={true}
                                    values={
                                      field.value
                                        ? [
                                            {
                                              value: field.value,
                                              label:
                                                survey.questionMap![field.value]
                                                  .data.title,
                                            },
                                          ]
                                        : []
                                    }
                                    onChangeValues={(fieldOption) => {
                                      if (
                                        !fieldOption[0] ||
                                        field.value !== fieldOption[0].value
                                      ) {
                                        form.setFieldValue(
                                          `notifications.${index}.conditionQuestionValues`,
                                          []
                                        );
                                        form.setFieldValue(
                                          `notifications.${index}.conditionQuestionId`,
                                          fieldOption[0]
                                            ? fieldOption[0].value
                                            : null
                                        );
                                      }
                                    }}
                                    placeholder={
                                      i18n.t('conditionQuestion', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                    label={
                                      i18n.t('conditionQuestion', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  />
                                  {field.value &&
                                    (() => {
                                      const choices = getChoices(
                                        // @ts-expect-error it's ok
                                        survey.questionMap[
                                          form.values.notifications[index]
                                            .conditionQuestionId
                                        ].data,
                                        tags
                                      ).reduce(
                                        (current: IOption[], item: any) => {
                                          current.push({
                                            value: item.value,
                                            label:
                                              item.text || 'no-label-found',
                                          });
                                          return current;
                                        },
                                        [] as IOption[]
                                      );
                                      return (
                                        <Select
                                          options={choices}
                                          disabled={readOnly}
                                          isRequired={true}
                                          allowRemove={false}
                                          values={
                                            form.values.notifications[
                                              index
                                            ].conditionQuestionValues?.map(
                                              (v: string) => {
                                                return {
                                                  value: v,
                                                  label: (
                                                    choices.find(
                                                      (it) =>
                                                        it.value?.toString() ===
                                                        v
                                                    ) || { label: 'removed' }
                                                  ).label,
                                                };
                                              }
                                            ) ?? []
                                          }
                                          onChangeValues={(fieldOption) => {
                                            form.setFieldValue(
                                              `notifications.${index}.conditionQuestionValues`,
                                              fieldOption.map((e) => e.value) ??
                                                []
                                            );
                                          }}
                                          placeholder={
                                            i18n.t('changedQuestionValue', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                          label={
                                            i18n.t('changedQuestionValue', {
                                              ns: 'survey',
                                            }) as string
                                          }
                                          multiChoice={true}
                                        />
                                      );
                                    })()}
                                </div>
                              )}
                            />
                          )}

                          <Field
                            name={`notifications.${index}.messagePattern`}
                            render={({ form, field }: FieldProps) => (
                              <div style={{ flex: '3' }}>
                                <MentionsInput
                                  value={field.value || ''}
                                  onChange={(_: any, newValue: string) => {
                                    form.setFieldValue(
                                      `notifications.${index}.messagePattern`,
                                      newValue
                                    );
                                  }}
                                  forceSuggestionsAboveCursor={true}
                                  allowSuggestionsAboveCursor={true}
                                  allowSpaceInQuery={true}
                                  className="surveySMSPattern"
                                  placeholder={
                                    i18n.t('messagePattern', {
                                      ns: 'survey',
                                    }) as string
                                  }
                                >
                                  <Mention
                                    trigger={'%'}
                                    data={notificationsPatternData}
                                    markup={'%__id__%'}
                                    displayTransform={(id: string) =>
                                      '(' +
                                      (notificationsPatternData.find(
                                        (it) => it.id === id
                                      )?.display || id) +
                                      ')'
                                    }
                                    appendSpaceOnAdd={true}
                                  />
                                  <Mention
                                    trigger={'٪'}
                                    data={notificationsPatternData}
                                    markup={'%__id__%'}
                                    displayTransform={(id: string) =>
                                      '(' +
                                      (notificationsPatternData.find(
                                        (it) => it.id === id
                                      )?.display || id) +
                                      ')'
                                    }
                                    appendSpaceOnAdd={true}
                                  />
                                </MentionsInput>
                              </div>
                            )}
                          />
                        </div>
                      </NotifContainer>
                    ))}
                  </div>
                )}
              />
              <div></div>
            </Container>

            {supportOrOwner && editNotifGroup}
            <div className="row">
              <div className="col s12 m6">
                <Field
                  name="periodType"
                  type="select"
                  render={({ form, field }: FieldProps) => (
                    <Select
                      options={periodType}
                      disabled={readOnly}
                      isRequired={true}
                      allowRemove={false}
                      values={
                        field.value
                          ? [
                              {
                                value: field.value,
                                label:
                                  field.value === 'none'
                                    ? (i18n.t('disabled', {
                                        ns: 'survey',
                                      }) as string)
                                    : (i18n.t(`${field.value}`, {
                                        ns: 'survey',
                                      }) as string),
                              },
                            ]
                          : []
                      }
                      onChangeValues={(fieldOption) => {
                        form.setFieldValue(
                          'periodType',
                          fieldOption[0] ? fieldOption[0].value : null
                        );
                      }}
                      placeholder={
                        i18n.t('periodType', { ns: 'survey' }) as string
                      }
                      label={i18n.t('periodType', { ns: 'survey' }) as string}
                    />
                  )}
                />
              </div>
              <div className="col s12 m6">
                <Field
                  name="scoreMethod"
                  type="select"
                  render={({ form, field }: FieldProps) => (
                    <Select
                      options={scoreMethodOptions}
                      disabled={readOnly}
                      isRequired={true}
                      allowRemove={false}
                      values={
                        field.value
                          ? [
                              {
                                value: field.value,
                                label: i18n.t(`${field.value}`, {
                                  ns: 'survey',
                                }) as string,
                              },
                            ]
                          : []
                      }
                      onChangeValues={(fieldOption) => {
                        form.setFieldValue(
                          'scoreMethod',
                          fieldOption[0] ? fieldOption[0].value : null
                        );
                      }}
                      placeholder={
                        i18n.t('scoreMethod', { ns: 'survey' }) as string
                      }
                      label={i18n.t('scoreMethod', { ns: 'survey' }) as string}
                    />
                  )}
                />
              </div>
              {(supportOrOwner || isAdvancedAdmin) && (
                <div className="col s12 m6">
                  <Field
                    name="visibilityStrategy"
                    type="select"
                    render={({ form, field }: FieldProps) => (
                      <Select
                        options={visibilityStrategyOptions}
                        disabled={readOnly}
                        isRequired={true}
                        allowRemove={false}
                        values={
                          field.value
                            ? [
                                {
                                  value: field.value,
                                  label: i18n.t(
                                    `${'visibility_' + field.value}`,
                                    { ns: 'survey' }
                                  ) as string,
                                },
                              ]
                            : []
                        }
                        onChangeValues={(fieldOption) => {
                          form.setFieldValue(
                            'visibilityStrategy',
                            fieldOption[0] ? fieldOption[0].value : null
                          );
                        }}
                        placeholder={
                          i18n.t('visibilityStrategy', {
                            ns: 'survey',
                          }) as string
                        }
                        label={
                          i18n.t('visibilityStrategy', {
                            ns: 'survey',
                          }) as string
                        }
                      />
                    )}
                  />
                </div>
              )}
              {(supportOrOwner || isAdvancedAdmin) && (
                <div className="col s12 m6">
                  <Field
                    name="stageQuestion"
                    type="select"
                    render={({ form, field }: FieldProps) => (
                      <Select
                        options={questions}
                        disabled={readOnly}
                        isRequired={false}
                        allowRemove={true}
                        values={
                          field.value
                            ? [
                                {
                                  value: field.value,
                                  label:
                                    survey.questionMap![field.value].data.title,
                                },
                              ]
                            : []
                        }
                        onChangeValues={(fieldOption) => {
                          form.setFieldValue(
                            'stageQuestion',
                            fieldOption[0] ? fieldOption[0].value : null
                          );
                        }}
                        label={
                          i18n.t('stageQuestion', { ns: 'survey' }) as string
                        }
                      />
                    )}
                  />
                </div>
              )}
            </div>
            {instanceType === 'User' && (
              <div className="col s12 m6">
                <Field
                  name="showResponseToTarget"
                  render={({ field }: FieldProps) => (
                    <Input
                      {...field}
                      id="showResponseToTarget"
                      type="checkbox"
                      disabled={readOnly}
                      label={
                        i18n.t('showResponseToTarget', {
                          ns: 'survey',
                        }) as string
                      }
                      checked={!!showResponseToTarget}
                    />
                  )}
                />
              </div>
            )}
            <div className="col s12 m6">
              <Field
                name="autoLock"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    id="autoLock"
                    type="checkbox"
                    disabled={readOnly}
                    label={i18n.t('autoLock', { ns: 'survey' }) as string}
                    checked={autoLock}
                  />
                )}
              />
            </div>
            {supportOrOwner && (
              <div className="col s12 m6">
                <Field
                  name="recommendRefill"
                  render={({ field }: FieldProps) => (
                    <Input
                      {...field}
                      id="recommendRefill"
                      type="checkbox"
                      disabled={readOnly}
                      label={
                        i18n.t('recommendRefill', { ns: 'survey' }) as string
                      }
                      checked={recommendRefill}
                    />
                  )}
                />
              </div>
            )}

            <div className="col s12 m6">
              <Field
                name="keepResponsesAnonymous"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    id="keepResponsesAnonymous"
                    type="checkbox"
                    disabled={readOnly}
                    label={
                      i18n.t('keepResponsesAnonymous', {
                        ns: 'survey',
                      }) as string
                    }
                    checked={keepResponsesAnonymous}
                  />
                )}
              />
            </div>
            <div className="col s12 m6">
              <Field
                name="offerToFillAtFirst"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    id="offerToFillAtFirst"
                    type="checkbox"
                    disabled={readOnly}
                    label={
                      i18n.t('offerToFillAtFirst', { ns: 'survey' }) as string
                    }
                    checked={offerToFillAtFirst}
                  />
                )}
              />
            </div>
            <div className="col s12 m6">
              <Field
                name="trackResponseChanges"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    id="trackResponseChanges"
                    type="checkbox"
                    disabled={readOnly}
                    label={
                      i18n.t('trackResponseChanges', { ns: 'survey' }) as string
                    }
                    checked={trackResponseChanges}
                  />
                )}
              />
            </div>
            <div className="col s12 m12">
              <Field
                name="keepUnique"
                render={({ field, form }: FieldProps) => (
                  <Input
                    {...field}
                    id="keepUnique"
                    type="checkbox"
                    disabled={readOnly}
                    label={i18n.t('keepUnique', { ns: 'survey' }) as string}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        form.setFieldValue('uniquePerUser', false);
                        form.setFieldValue('uniquePerInstance', false);
                      }
                      form.setFieldValue('keepUnique', e.target.checked);
                    }}
                    checked={keepUnique}
                  />
                )}
              />
            </div>
            {
              <>
                <div className="col s12 m6">
                  <Field
                    name="uniquePerUser"
                    render={({ field, form }: FieldProps) => (
                      <Input
                        {...field}
                        id="uniquePerUser"
                        type="checkbox"
                        disabled={readOnly}
                        label={
                          i18n.t('uniquePerUser', { ns: 'survey' }) as string
                        }
                        onChange={(e) => {
                          if (e.target.checked)
                            form.setFieldValue('keepUnique', true);
                          form.setFieldValue('uniquePerUser', e.target.checked);
                        }}
                        checked={uniquePerUser}
                      />
                    )}
                  />
                </div>
                <div className="col s12 m6">
                  <Field
                    name="uniquePerInstance"
                    render={({ field, form }: FieldProps) => (
                      <Input
                        {...field}
                        id="uniquePerInstance"
                        type="checkbox"
                        disabled={readOnly}
                        label={
                          i18n.t('uniquePerInstance', {
                            ns: 'survey',
                          }) as string
                        }
                        onChange={(e) => {
                          if (e.target.checked)
                            form.setFieldValue('keepUnique', true);
                          form.setFieldValue(
                            'uniquePerInstance',
                            e.target.checked
                          );
                        }}
                        checked={uniquePerInstance}
                      />
                    )}
                  />
                </div>
                {/*<div className="col s12 m6">
                  <Field
                    name="uniquePerTags"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        id="uniquePerTags"
                        type="checkbox"
                        disabled={readOnly}
                        label={messages.uniquePerTags}
                        checked={uniquePerTags}
                      />
                    )}
                  />
                </div>
                <div className="col s12 m6">
                  <Field
                    name="keepSubmitQuestionList"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        id="keepSubmitQuestionList"
                        type="checkbox"
                        disabled={readOnly}
                        label={messages.keepSubmitQuestionList}
                        checked={keepSubmitQuestionList}
                      />
                    )}
                  />
                </div>
                <div className="col s12 m6">
                  <Field
                    name="randomizeOrder"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        id="randomizeOrder"
                        type="checkbox"
                        disabled={readOnly}
                        label={messages.randomizeOrder}
                        checked={randomizeOrder}
                      />
                    )}
                  />
                </div>*/}
              </>
            }

            {supportOrOwner && (
              <div className="col s12 m6">
                <Field
                  name="sensitiveChangeRequiresEditor"
                  render={({ field }: FieldProps) => (
                    <Input
                      {...field}
                      id="sensitiveChangeRequiresEditor"
                      type="checkbox"
                      disabled={readOnly}
                      label={
                        i18n.t('sensitiveChangeRequiresEditor', {
                          ns: 'survey',
                        }) as string
                      }
                      checked={sensitiveChangeRequiresEditor}
                    />
                  )}
                />
              </div>
            )}
            <div className="col s12 m6">
              <Field
                name="restrictionOfCompletion"
                render={({ form, field }: FieldProps) => (
                  <Input
                    {...field}
                    type="checkbox"
                    id="restrictionOfCompletion"
                    disabled={readOnly}
                    label={
                      i18n.t('restrictionOfCompletion', {
                        ns: 'survey',
                      }) as string
                    }
                    checked={restrictionOfCompletion}
                    onChange={(e) => {
                      form.setFieldValue(
                        'restrictionOfCompletion',
                        e.target.checked
                      );
                      if (!e.target.checked)
                        form.setFieldValue('fillValidPeriod', {
                          fromOffsetDaysRelativeToStartOfPeriod: null,
                          toOffsetDaysRelativeToEndOfPeriod: null,
                        });
                    }}
                  />
                )}
              />
              {restrictionOfCompletion && (
                <div className="flex items-center gap-5 my-2">
                  <Field
                    name="fillValidPeriod.fromOffsetDaysRelativeToStartOfPeriod"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="number"
                        label={
                          i18n.t('startOffset', { ns: 'survey' }) as string
                        }
                        id="fillValidPeriod.fromOffsetDaysRelativeToStartOfPeriod"
                      />
                    )}
                  />
                  <Field
                    name="fillValidPeriod.toOffsetDaysRelativeToEndOfPeriod"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="number"
                        label={i18n.t('endOffset', { ns: 'survey' }) as string}
                        id="initialFillValidPeriod.toOffsetDaysRelativeToEndOfPeriod"
                      />
                    )}
                  />
                </div>
              )}
            </div>
            <div className="col s12 m12">
              <Field
                name="versionTitle"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    id="versionTitle"
                    type="text"
                    disabled={readOnly}
                    label={i18n.t('versionTitle', { ns: 'survey' }) as string}
                  />
                )}
              />
            </div>
            <div className="col s12 m12">
              <Field
                name="versionDescription"
                render={({ field }: FieldProps) => (
                  <Input
                    {...field}
                    id="versionDescription"
                    disabled={readOnly}
                    type="text"
                    label={
                      i18n.t('versionDescription', { ns: 'survey' }) as string
                    }
                  />
                )}
              />
            </div>

            {!readOnly && (
              <div className="col s12">
                <Button loading={saving} type="submit" color="green">
                  {i18n.t('save', { ns: 'survey' }) as string}
                </Button>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

export default SurveySettingForm;
